import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';

export interface IssuedKeyValues {
  partnerName: string;
  partnerKey: string | null;
  key: string | null;
}

interface IssuedKeysDialogProps {
  /** null = 닫힘. 원문은 이 prop 으로만 들어오고, 닫으면 호출부가 null 로 버린다(저장소·캐시에 남기지 않음). */
  issued: IssuedKeyValues | null;
  onClose: () => void;
}

const KeyRow = ({ label, value, testId }: { label: string; value: string; testId: string }) => {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // 클립보드가 막힌 환경 — 값이 화면에 그대로 있어 직접 복사할 수 있다.
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
        {label}
      </span>
      <div className="flex items-center gap-2">
        <code
          className="min-w-0 flex-1 break-all rounded px-2 py-1.5 font-mono text-[13px]"
          style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)', color: 'var(--content-strong)' }}
          data-testid={testId}
          data-sentry-mask
        >
          {value}
        </code>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void copy()}
          aria-label={t('openApiPartners.keys.copyAria', { label, defaultValue: '{{label}} 복사' })}
        >
          {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          {copied ? t('openApiPartners.keys.copied', '복사됨') : t('openApiPartners.keys.copy', '복사')}
        </Button>
      </div>
    </div>
  );
};

/** 발급된 partner_key / key 원문 1회 표시(등록·재발급 공용). 재발급하지 않은 쪽(null)은 그리지 않는다. */
export const IssuedKeysDialog = ({ issued, onClose }: IssuedKeysDialogProps) => {
  const { t } = useTranslation('common');
  return (
    <Dialog open={issued != null} onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-w-[560px]" data-testid="issued-keys-dialog">
        <DialogHeader>
          <DialogTitle>{t('openApiPartners.keys.title', '발급된 키')}</DialogTitle>
          <DialogDescription>{issued?.partnerName}</DialogDescription>
        </DialogHeader>
        <p
          role="alert"
          className="rounded px-3 py-2 text-[13px]"
          style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
        >
          {t(
            'openApiPartners.keys.once',
            '이 창을 닫으면 키를 다시 볼 수 없습니다. 지금 복사해 제휴사에 안전하게 전달하세요. 잃어버리면 재발급해야 합니다.',
          )}
        </p>
        <div className="flex flex-col gap-3 py-1">
          {issued?.partnerKey ? (
            <KeyRow label={t('openApiPartners.fields.partnerKey', '제휴사 키 (partner_key)')} value={issued.partnerKey} testId="issued-partner-key" />
          ) : null}
          {issued?.key ? (
            <KeyRow label={t('openApiPartners.fields.shopKey', '쇼핑몰 키 (key)')} value={issued.key} testId="issued-shop-key" />
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {t('openApiPartners.keys.close', '확인했습니다')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
